/**
 * Notices for AI changes still awaiting Accept/Reject (R0-A).
 *
 * Outputs (Word export, the Submission Pack) carry a section's last accepted
 * text while its AI change waits for a decision. These are the messages that
 * tell the user so, and the one shown when Reject cannot undo a change.
 *
 * Copy approved by Kapil 2026-10-07 (the two "left out" strings shipped as
 * drafted, pending his read). Every frontend string for this feature lives
 * here so a wording change is one edit. The backend's
 * strings (MANIFEST.xlsx notes) live in
 * `backend/src/lib/pending-ai-change-notices.ts`.
 */

import { toast } from 'sonner'
import type { PackPendingAiChange, PendingAiChangeNotice } from '@/lib/api'

// ===================
// Copy
// ===================

/** Word export: sections that went out with their last accepted text. */
export const EXPORT_NOTICE =
  "Exported with last accepted text. {sections} {has} AI changes you haven't accepted, so the file uses their last accepted text. Accept or reject the changes, then export again."

/** Word export: sections left out because their only text is an unaccepted AI change. */
export const EXPORT_LEFT_OUT =
  "{sections} {was} left out because the only text is an AI change you haven't accepted."

/** Submission Pack: documents that went out with last accepted text. */
export const PACK_NOTICE =
  'Pack exported. {count} {documents} {use} last accepted text because AI changes are waiting for review. See the Note column in MANIFEST.xlsx.'

/** Reject could not undo the suggestion because the section changed since. */
export const REJECT_CONFLICT =
  "This section changed after the AI suggestion, so the suggestion wasn't undone. Use version history if you need the earlier text."

// ===================
// Formatting
// ===================

const MAX_NAMED_SECTIONS = 3
const MAX_SECTION_NAME_LENGTH = 60

function shortName(name: string): string {
  const trimmed = name.trim()
  return trimmed.length > MAX_SECTION_NAME_LENGTH
    ? `${trimmed.slice(0, MAX_SECTION_NAME_LENGTH - 1).trimEnd()}…`
    : trimmed
}

/** "A", "A and B", "A, B and C", "A, B, C and 2 more". */
export function listSectionNames(names: readonly string[]): string {
  const shown = names.slice(0, MAX_NAMED_SECTIONS).map(shortName)
  const rest = names.length - shown.length
  if (rest > 0) return `${shown.join(', ')} and ${rest} more`
  if (shown.length <= 1) return shown[0] ?? ''
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
}

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match)
}

/**
 * The notice for a Word export, or null when nothing was held back. One
 * sentence per kind: sections that used last accepted text, then any left out.
 */
export function exportNoticeText(changes: readonly PendingAiChangeNotice[]): string | null {
  if (changes.length === 0) return null
  const used = changes.filter((change) => change.hasAcceptedVersion).map((change) => change.sectionName)
  const leftOut = changes.filter((change) => !change.hasAcceptedVersion).map((change) => change.sectionName)
  const parts: string[] = []
  if (used.length > 0) {
    parts.push(fill(EXPORT_NOTICE, {
      sections: listSectionNames(used),
      has: used.length === 1 ? 'has' : 'have',
    }))
  }
  if (leftOut.length > 0) {
    parts.push(fill(EXPORT_LEFT_OUT, {
      sections: listSectionNames(leftOut),
      was: leftOut.length === 1 ? 'was' : 'were',
    }))
  }
  return parts.join(' ')
}

/** The notice for a Submission Pack export, or null when nothing was held back. */
export function packNoticeText(items: readonly PackPendingAiChange[]): string | null {
  if (items.length === 0) return null
  const count = items.length
  return fill(PACK_NOTICE, {
    count: String(count),
    documents: count === 1 ? 'document' : 'documents',
    use: count === 1 ? 'uses' : 'use',
  })
}

/**
 * After a Word export: the usual success toast, or — when sections were held
 * back — a warning naming them, kept on screen long enough to read.
 */
export function toastExportResult(successMessage: string, changes: readonly PendingAiChangeNotice[]): void {
  const notice = exportNoticeText(changes)
  if (notice) {
    toast.warning(notice, { duration: 12_000 })
    return
  }
  toast.success(successMessage)
}
